import { v4 as uuidv4 } from 'uuid';

import { DeviceType, UNAUTHORIZED_MESSAGE } from '../constants';
import { createMarketingTouch, MarketingTouchPayload } from '../data/queries';

import { logError } from './logging';
import { convertQueryStringToJSON, parseQueryString } from './queryServices';

export const getCampaignRefCode = (queryString: string) => {
  const queryParams =
    queryString && queryString.length
      ? parseQueryString(queryString)
      : new Map();

  const campaignRefCode = queryParams.get('crc') || 'npc-direct-reg';
  return campaignRefCode;
};

// crafts the marketing metadata from the query string and referrer info in session storage
const createMarketingMetadata = (queryString: string) => {
  const queryParamMap =
    (queryString &&
      queryString.length &&
      convertQueryStringToJSON(queryString)) ||
    {};

  const initialVisitedPageInfo =
    JSON.parse(sessionStorage.getItem('initialVisitedPageInfo')) || {};

  return JSON.stringify({
    ...queryParamMap,
    source_referrer: initialVisitedPageInfo.referrer,
    touchpoint_url: initialVisitedPageInfo.touchpoint,
  });
};

export const makeMarketingTouchPayload = (
  queryString: string,
  deviceType?: DeviceType,
  applicationId?: string,
  offerCode?: string
): MarketingTouchPayload => {
  const queryParams =
    queryString && queryString.length
      ? parseQueryString(queryString)
      : new Map();
  const marketingMetaDataJson = createMarketingMetadata(queryString);

  // Use tracking IDs to allow BE to update touches related to same application / user
  let persistentTouchTrackingId = localStorage.getItem(
    'persistentTouchTrackingID'
  );
  let sessionTouchTrackingId = sessionStorage.getItem('sessionTouchTrackingID');

  if (!persistentTouchTrackingId) {
    const LOCAL_GUID = uuidv4();
    localStorage.setItem('persistentTouchTrackingID', LOCAL_GUID);
    persistentTouchTrackingId = LOCAL_GUID;
  }

  if (!sessionTouchTrackingId) {
    const SESSION_GUID = uuidv4();
    sessionStorage.setItem('sessionTouchTrackingID', SESSION_GUID);
    sessionTouchTrackingId = SESSION_GUID;
  }

  const externalApplicationId = applicationId || null;
  const campaignRefCode = getCampaignRefCode(queryString);
  const sessionlandingPageRefCode =
    sessionStorage.getItem('landingPageRefCode');
  const landingPageRefCode =
    queryParams.get('q') || sessionlandingPageRefCode || 'npc-direct-reg';

  return {
    externalApplicationId,
    campaignRefCode,
    landingPageRefCode,
    persistentTouchTrackingId,
    sessionTouchTrackingId,
    marketingMetaDataJson,
    deviceType,
    offerCode,
  };
};

export const sendMarketingTouch = async (
  queryString: string,
  deviceType?: DeviceType,
  applicationId?: string,
  offerCode?: string
) => {
  const marketingTouchPayload = makeMarketingTouchPayload(
    queryString,
    deviceType,
    applicationId,
    offerCode
  );

  // create the marketing touch and log any errors (failures shouldn't block users in the funnel)
  return createMarketingTouch(marketingTouchPayload).catch((err: any) => {
    if (err.message !== UNAUTHORIZED_MESSAGE) {
      logError(err, {
        errorName: err.name || 'UtilityError',
        errorMessage: err.message || 'Unexpected error occurred',
        component: 'marketingTouch',
        function: 'sendMarketingTouch',
      });
    }
  });
};
